import { useQuery } from '@tanstack/react-query';
import { Service, useProDataService } from 'services/data.service';
import { useDataServiceQueryResult } from 'hooks/useDataServiceQueryResult';
import { useAppStore } from 'hooks/state/useAppStore';
import { FilterValue } from 'components/breadcrumb/breadcrumbModel';
import { ExtendedAttributeCode } from 'pages/trading/model';
import {
  ProfitabilityStatus,
  ProfitabilityStatusCounts,
  RankedMoverRow,
  RankedMoversData,
  SupportedAttributeCode,
} from './types';
import { DataEntry, DataResult } from 'models/data';
import { useContext } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { DataRequest, useDataRequest } from 'hooks/useDataRequest';
import { useDataEntriesInTables } from 'components/table/useDataEntriesInTables';

function mapStatusCountsResponse(response: DataResult): ProfitabilityStatusCounts {
  return {
    atRiskCount: response.values.atRiskCount ?? 0,
    atRiskCountPrev: response.values.atRiskCountPrev ?? 0,
    criticalCount: response.values.criticalCount ?? 0,
    criticalCountPrev: response.values.criticalCountPrev ?? 0,
  };
}

export function useProfitabilityOverviewKpis(requestData: DataRequest) {
  const { fetchData } = useProDataService(Service.Profitability);
  const { accountNumber } = useContext(TenantConfigContext);

  return useQuery({
    queryKey: ['profitability-kpis', accountNumber, requestData],
    queryFn: async (): Promise<DataResult | null> => {
      const response = await fetchData('kpis', requestData);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data ?? null;
    },
    staleTime: 0,
  });
}

export function useProfitabilityFeesAndCosts(requestData: DataRequest) {
  const { fetchData } = useProDataService(Service.Profitability);

  return useDataServiceQueryResult<DataResult>(
    requestData,
    data => fetchData('fees-and-costs', data),
    response => response,
    ['fees-and-costs']
  );
}

export function useProfitabilityStatusFilterCounts(attributeCode: string, queryKey: string) {
  const { fetchData } = useProDataService(Service.Profitability);

  const requestData = useDataRequest();

  return useDataServiceQueryResult(
    requestData,
    data => fetchData(`${attributeCode}/kpis`, data),
    response => mapStatusCountsResponse(response),
    [queryKey]
  );
}

function resolveDrillDownFilters(
  filters: Record<ExtendedAttributeCode, FilterValue[]>,
  appCategories: string[] | undefined,
  appCountries: string[] | undefined,
  appMarketplaces: string[] | undefined,
  appSeasons: string[] | undefined,
  appGenders: string[] | undefined
) {
  return {
    resolvedCategories: (filters.prdCategory as string[] | undefined) ?? appCategories,
    resolvedCountries: (filters.chnCountry as string[] | undefined) ?? appCountries,
    resolvedMarketplaces: (filters.chnMarketplace as string[] | undefined) ?? appMarketplaces,
    resolvedSeasons: (filters.prdSeason as string[] | undefined) ?? appSeasons,
    resolvedGenders: (filters.prdGender as string[] | undefined) ?? appGenders,
    resolvedFulfillmentMethods: filters.chnFulfillmentMethod as string[] | undefined,
    resolvedProductNumbers: filters.prdNumber as string[] | undefined,
  };
}

export function useProfitabilityDrillDownKpis(filters: Record<ExtendedAttributeCode, FilterValue[]>, queryKey: string) {
  const { fetchData } = useProDataService(Service.Profitability);
  const { countries, marketplaces, categories, seasons, genders } = useAppStore();

  const {
    resolvedCategories,
    resolvedCountries,
    resolvedMarketplaces,
    resolvedSeasons,
    resolvedGenders,
    resolvedFulfillmentMethods,
    resolvedProductNumbers,
  } = resolveDrillDownFilters(filters, categories, countries, marketplaces, seasons, genders);

  const requestData = useDataRequest();

  const refinedRequestData = {
    ...requestData,
    filters: {
      ...requestData.filters,
      categories: resolvedCategories,
      countries: resolvedCountries,
      marketplaces: resolvedMarketplaces,
      seasons: resolvedSeasons,
      genders: resolvedGenders,
      productNumbers: resolvedProductNumbers,
      fulfillmentMethods: resolvedFulfillmentMethods,
    },
  };

  return useDataServiceQueryResult(
    refinedRequestData,
    data => fetchData('kpis', data),
    response => response,
    [queryKey]
  );
}

export const useRequestDataForDrillDownPage = (filters: Record<ExtendedAttributeCode, FilterValue[]>) => {
  const { countries, marketplaces, categories, seasons, genders } = useAppStore();

  const {
    resolvedCategories,
    resolvedCountries,
    resolvedMarketplaces,
    resolvedSeasons,
    resolvedGenders,
    resolvedFulfillmentMethods,
    resolvedProductNumbers,
  } = resolveDrillDownFilters(filters, categories, countries, marketplaces, seasons, genders);

  const requestData = useDataRequest();

  return {
    ...requestData,
    filters: {
      ...requestData.filters,
      categories: resolvedCategories,
      countries: resolvedCountries,
      marketplaces: resolvedMarketplaces,
      seasons: resolvedSeasons,
      genders: resolvedGenders,
      productNumbers: resolvedProductNumbers,
      fulfillmentMethods: resolvedFulfillmentMethods,
    },
  };
};

export const useProfitabilityTableData = (attributeCode: string, requestData: DataRequest, queryKey: string) => {
  const { fetchData } = useProDataService(Service.Profitability);
  const { dataEntryToDataEntryRowRecord } = useDataEntriesInTables();

  return useDataServiceQueryResult(
    requestData,
    data => fetchData(attributeCode, data),
    response =>
      (response.sets.rows ?? []).map(entry => ({
        ...dataEntryToDataEntryRowRecord(entry),
        status: (entry.attributes.status as ProfitabilityStatus) ?? ProfitabilityStatus.HEALTHY,
      })),
    [queryKey]
  );
};

export const useProfitabilityBreakdownRows = (attributeCode: string, queryKey: string) => {
  const requestData = useDataRequest();

  return useProfitabilityTableData(attributeCode, requestData, queryKey);
};

export const useProfitabilityLensBreakdownRows = (
  attributeCode: string,
  requestData: DataRequest,
  queryKey: string
) => {
  return useProfitabilityTableData(attributeCode, requestData, queryKey);
};

export function useProfitabilityRankedMovers(requestData: DataRequest) {
  const { fetchData } = useProDataService(Service.Profitability);
  const { dataEntryToDataEntryRowRecord } = useDataEntriesInTables();

  const mapEntry = (entry: DataEntry): RankedMoverRow => ({
    ...dataEntryToDataEntryRowRecord(entry),
    entityType: entry.attributes.entityType as SupportedAttributeCode,
    entityValue: entry.attributes[entry.attributes.entityType] ?? '',
  });

  return useDataServiceQueryResult<RankedMoversData, DataResult>(
    requestData,
    data => fetchData('ranked-movers', data),
    response => ({
      gainers: (response.sets.gainers ?? []).map(mapEntry),
      decliners: (response.sets.decliners ?? []).map(mapEntry),
    }),
    ['ranked-movers']
  );
}
